/**
 * HTTP service for the requirements feature (PX-W7.2, TASK-60028F48): the
 * project's requirements register, its three lifecycle acts (confirm /
 * reject / supersede) and the version-scoped bulk import. One transport,
 * bare success bodies — `JsonResults.ok` never wraps (CONFIRMED,
 * `infrastructure/http/JsonResults.java`) — and every rejection is left to
 * `apiClient`/`toApiError` to map; nobody here parses a response body by
 * hand or hard-codes a status.
 */

import { apiClient } from '@/services'
import type {
  BulkImportResult,
  RequirementConfirmInput,
  RequirementCreateInput,
  RequirementFilters,
  RequirementRejectInput,
  RequirementRow,
  RequirementSupersedeInput,
  RequirementUpdateInput
} from '../types'

const base = (orgId: string, projectId: string): string => `/${orgId}/projects/${projectId}`

export const requirementsService = {
  /**
   * The register's rows, R-001 first, every supplied filter AND-combined
   * (`GET …/requirements`). A cleared filter contributes NO query
   * parameter — never `?status=`.
   */
  async listRequirements(
    orgId: string,
    projectId: string,
    filters: RequirementFilters = {}
  ): Promise<RequirementRow[]> {
    const qs = new URLSearchParams()
    if (filters.status) qs.set('status', filters.status)
    if (filters.verdict) qs.set('verdict', filters.verdict)
    if (filters.elementKey) qs.set('elementKey', filters.elementKey)
    if (filters.ownerId) qs.set('ownerId', filters.ownerId)
    if (filters.versionId) qs.set('versionId', filters.versionId)
    const query = qs.toString()
    return apiClient.get<RequirementRow[]>(
      `${base(orgId, projectId)}/requirements${query ? `?${query}` : ''}`
    )
  },

  /** One requirement by its `R-nnn` ref (`GET …/requirements/:requirementRef`). */
  async getRequirement(orgId: string, projectId: string, ref: string): Promise<RequirementRow> {
    return apiClient.get<RequirementRow>(`${base(orgId, projectId)}/requirements/${ref}`)
  },

  /** Raise a human-authored requirement (`POST …/requirements`, returns 200). */
  async createRequirement(
    orgId: string,
    projectId: string,
    input: RequirementCreateInput
  ): Promise<RequirementRow> {
    return apiClient.post<RequirementRow>(`${base(orgId, projectId)}/requirements`, input)
  },

  /**
   * Whole-content replace of a still-`PROPOSED` row (`PUT
   * …/requirements/:requirementRef`) — the form-edited fields only, never
   * the whole `RequirementRow` back.
   */
  async updateRequirement(
    orgId: string,
    projectId: string,
    ref: string,
    input: RequirementUpdateInput
  ): Promise<RequirementRow> {
    return apiClient.put<RequirementRow>(`${base(orgId, projectId)}/requirements/${ref}`, input)
  },

  /** Accept a requirement into the build (`POST …/:requirementRef/confirm`). An empty body is valid. */
  async confirmRequirement(
    orgId: string,
    projectId: string,
    ref: string,
    body: RequirementConfirmInput = {}
  ): Promise<RequirementRow> {
    return apiClient.post<RequirementRow>(`${base(orgId, projectId)}/requirements/${ref}/confirm`, body)
  },

  /** Decline a requirement with a required, recorded reason (`POST …/:requirementRef/reject`). */
  async rejectRequirement(
    orgId: string,
    projectId: string,
    ref: string,
    body: RequirementRejectInput
  ): Promise<RequirementRow> {
    return apiClient.post<RequirementRow>(`${base(orgId, projectId)}/requirements/${ref}/reject`, body)
  },

  /** Replace a confirmed requirement with a later one, or simply retire it (`POST …/:requirementRef/supersede`). */
  async supersedeRequirement(
    orgId: string,
    projectId: string,
    ref: string,
    body: RequirementSupersedeInput = {}
  ): Promise<RequirementRow> {
    return apiClient.post<RequirementRow>(`${base(orgId, projectId)}/requirements/${ref}/supersede`, body)
  },

  /**
   * Decompose a version's concluded intakes into proposed requirements
   * (`POST …/requirements/bulk-import`). The body carries `versionId` and
   * NOTHING else — there is no knob and no paste-rows form.
   */
  async bulkImportRequirements(
    orgId: string,
    projectId: string,
    body: { versionId: string }
  ): Promise<BulkImportResult> {
    return apiClient.post<BulkImportResult>(`${base(orgId, projectId)}/requirements/bulk-import`, body)
  }
}
